import React, { useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import useRevealSection from "../hooks/useRevealSection";
import {
  ConsentCategory,
  getConsent,
  onConsentChange,
  openConsentSettings,
} from "../utils/cookieConsent";

interface FormValues {
  name: string;
  email: string;
  number: string;
  product: string;
  platform: string;
  emailsubject: string;
  message: string;
}

const PRODUCT_OPTIONS = ["promptdoo", "incomeClock", "iosApp", "general"] as const;

// Produkte, die eine App sind und daher eine Betriebssystem-Abfrage benötigen.
const APP_PRODUCTS = ["incomeClock", "iosApp"];
const PLATFORM_OPTIONS = ["ios", "android", "mac"] as const;

const isAppProduct = (product: string) => APP_PRODUCTS.includes(product);

const TYPING_SPEED_MS = 55;

type HeadingTypingPhase = "pre" | "span" | "done";

const ContactForm: React.FC = () => {
  const { t } = useTranslation();
  const sectionRef = useRevealSection<HTMLElement>();
  const headingPre = t("contact.headingPre");
  const headingSpan = t("contact.headingSpan");
  const [typedHeadingPre, setTypedHeadingPre] = useState("");
  const [typedHeadingSpan, setTypedHeadingSpan] = useState("");
  const [headingPhase, setHeadingPhase] = useState<HeadingTypingPhase>("pre");
  const [headingReplayKey, setHeadingReplayKey] = useState(0);
  const [values, setValues] = useState<FormValues>({
    name: "",
    email: "",
    number: "",
    product: "",
    platform: "",
    emailsubject: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormValues>({
    name: "",
    email: "",
    number: "",
    product: "",
    platform: "",
    emailsubject: "",
    message: "",
  });

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null);
  const [consent, setConsentState] = useState<ConsentCategory | null>(() =>
    getConsent()
  );

  useEffect(() => onConsentChange(setConsentState), []);

  useEffect(() => {
    let cancelled = false;
    setTypedHeadingPre("");
    setTypedHeadingSpan("");
    setHeadingPhase("pre");

    const typeText = (text: string, setter: (value: string) => void) =>
      new Promise<void>((resolve) => {
        let i = 0;
        const id = setInterval(() => {
          if (cancelled) {
            clearInterval(id);
            resolve();
            return;
          }
          i++;
          setter(text.slice(0, i));
          if (i >= text.length) {
            clearInterval(id);
            resolve();
          }
        }, TYPING_SPEED_MS);
      });

    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    (async () => {
      await typeText(headingPre, setTypedHeadingPre);
      if (cancelled) return;
      setHeadingPhase("span");
      await wait(150);
      await typeText(headingSpan, setTypedHeadingSpan);
      if (cancelled) return;
      setHeadingPhase("done");
    })();

    return () => {
      cancelled = true;
    };
  }, [headingPre, headingSpan, headingReplayKey]);

  useEffect(() => {
    const el = sectionRef.current;
    if (
      !el ||
      typeof window === "undefined" ||
      !("IntersectionObserver" in window)
    ) {
      return;
    }

    let hasLeft = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (hasLeft) {
            setHeadingReplayKey((k) => k + 1);
            hasLeft = false;
          }
        } else {
          hasLeft = true;
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [sectionRef]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setValues((prev) => {
      const next = { ...prev, [name]: value };
      // Betriebssystem-Auswahl zurücksetzen, wenn kein App-Produkt (mehr) gewählt ist.
      if (name === "product" && !isAppProduct(value)) {
        next.platform = "";
      }
      return next;
    });
  };

  const validate = () => {
    let formErrors: FormValues = {
      name: "",
      email: "",
      number: "",
      product: "",
      platform: "",
      emailsubject: "",
      message: "",
    };
    let isValid = true;

    if (!values.name.trim()) {
      formErrors.name = t("contact.errors.name");
      isValid = false;
    }

    if (!values.product.trim()) {
      formErrors.product = t("contact.errors.product");
      isValid = false;
    }

    if (isAppProduct(values.product) && !values.platform.trim()) {
      formErrors.platform = t("contact.errors.platform");
      isValid = false;
    }

    if (!values.email.trim()) {
      formErrors.email = t("contact.errors.email");
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      formErrors.email = t("contact.errors.emailInvalid");
      isValid = false;
    }

    if (!values.message.trim()) {
      formErrors.message = t("contact.errors.message");
      isValid = false;
    }

    if (consent !== "all") {
      alert(t("contact.consentAlert"));
      isValid = false;
    } else if (!captchaToken) {
      alert(t("contact.captchaAlert"));
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setIsSubmitting(true);

      // E-Mail über EmailJS senden
      /*
      const service_id: string = process.env
        .REACT_APP_EMAILJS_SERVICE_ID as string;
      const template_id: string = process.env
        .REACT_APP_EMAILJS_TEMPLATE_ID as string;
      const emailjs_api_key: string = process.env
        .REACT_APP_EMAILJS_API_KEY as string;
      emailjs
        .send(
          service_id, // Service ID von EmailJS
          template_id, // Template ID von EmailJS
          {
            from_name: values.name,
            from_email: values.email,
            from_emailsubject: values.emailsubject,
            form_number: values.number,
            message: values.message,
            "g-recaptcha-response": captchaToken,
          },
          emailjs_api_key // User ID von EmailJS (API Key)
        )
        .then(
          (result: EmailJSResponseStatus) => {
            console.log(result.text);
            setIsSuccess(true);
          },
          (error: any) => {
            console.log(process.env);
            console.log(error.text);
            setIsSuccess(false);
          }
        )
        .finally(() => {
          setIsSubmitting(false);
        });
        */

      try {
        const response = await fetch(
          "https://grimmenstein.dev/contactfunction.php",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...values,
              product: values.product
                ? (t(`contact.products.${values.product}`) as string)
                : "",
              platform: values.platform
                ? (t(`contact.platforms.${values.platform}`) as string)
                : "",
              captchaToken,
            }),
          }
        );

        const data = await response.json();
        setIsSuccess(data.success);
      } catch (error) {
        console.error("Error:", error);
        setIsSuccess(false);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <section className="contact" id="contact" ref={sectionRef}>
      <h2 className="heading reveal">
        {typedHeadingPre}
        {headingPhase === "pre" && (
          <span className="typing-cursor" aria-hidden="true"></span>
        )}{" "}
        <span>
          {typedHeadingSpan}
          {(headingPhase === "span" || headingPhase === "done") && (
            <span className="typing-cursor" aria-hidden="true"></span>
          )}
        </span>
      </h2>
      <form onSubmit={handleSubmit} className="reveal reveal-d1">
        <div className="input-box">
          <div className="input-field">
            <input
              type="text"
              id="name"
              name="name"
              placeholder={t("contact.placeholders.name") as string}
              value={values.name}
              onChange={handleChange}
              required
            />
            {errors.name && <p style={{ color: "red" }}>{errors.name}</p>}
            <span className="focus"></span>
          </div>
          <div className="input-field">
            <input
              type="text"
              id="email"
              name="email"
              value={values.email}
              onChange={handleChange}
              placeholder={t("contact.placeholders.email") as string}
              required
            />
            {errors.email && <p style={{ color: "red" }}>{errors.email}</p>}

            <span className="focus"></span>
          </div>
        </div>

        <div className="select-field">
          <select
            id="product"
            name="product"
            value={values.product}
            onChange={handleChange}
            required
            aria-label={t("contact.placeholders.product") as string}
          >
            <option value="" disabled>
              {t("contact.placeholders.product")}
            </option>
            {PRODUCT_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {t(`contact.products.${option}`)}
              </option>
            ))}
          </select>
          {errors.product && <p style={{ color: "red" }}>{errors.product}</p>}
          <span className="focus"></span>
        </div>

        {isAppProduct(values.product) && (
          <div className="select-field">
            <select
              id="platform"
              name="platform"
              value={values.platform}
              onChange={handleChange}
              required
              aria-label={t("contact.placeholders.platform") as string}
            >
              <option value="" disabled>
                {t("contact.placeholders.platform")}
              </option>
              {PLATFORM_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`contact.platforms.${option}`)}
                </option>
              ))}
            </select>
            {errors.platform && (
              <p style={{ color: "red" }}>{errors.platform}</p>
            )}
            <span className="focus"></span>
          </div>
        )}

        <div className="input-box">
          <div className="input-field">
            <input
              type="number"
              id="number"
              name="number"
              value={values.number}
              onChange={handleChange}
              placeholder={t("contact.placeholders.phone") as string}
              required
            />
            <span className="focus"></span>
          </div>
          <div className="input-field">
            <input
              type="text"
              name="emailsubject"
              id="emailsubject"
              value={values.emailsubject}
              onChange={handleChange}
              placeholder={t("contact.placeholders.subject") as string}
              required
            />
            <span className="focus"></span>
          </div>
        </div>

        <div className="textarea-field">
          <textarea
            name="message"
            id="message"
            cols={30}
            rows={10}
            value={values.message}
            onChange={handleChange}
            placeholder={t("contact.placeholders.message") as string}
            required
          />
          {errors.message && <p style={{ color: "red" }}>{errors.message}</p>}

          <span className="focus"></span>
        </div>

        {/* Google reCAPTCHA v2 - lädt erst nach Einwilligung zu externen Diensten */}
        {consent === "all" ? (
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHAV2_KEY as string}
            onChange={handleCaptchaChange}
          />
        ) : (
          <p style={{ fontSize: "1.4rem" }}>
            {t("contact.recaptchaNeeded")}{" "}
            <button
              type="button"
              onClick={openConsentSettings}
              style={{
                background: "none",
                border: "none",
                textDecoration: "underline",
                cursor: "pointer",
                color: "inherit",
                padding: 0,
                font: "inherit",
              }}
            >
              {t("contact.openCookieSettings")}
            </button>
          </p>
        )}

        <div className="btn-box btns">
          <button type="submit" className="btn" disabled={isSubmitting}>
            {isSubmitting ? t("contact.sending") : t("contact.send")}
          </button>
        </div>

        {isSuccess === true && (
          <p style={{ color: "green" }}>{t("contact.success")}</p>
        )}
        {isSuccess === false && (
          <p style={{ color: "red" }}>{t("contact.error")}</p>
        )}
      </form>
    </section>
  );
};

export default ContactForm;
